import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/analytics-products/ModelHubAnalyticsConstants';
import {AVAILABLE_PAGE_SIZES, DEFAULT_PAGE_SIZE} from 'nav/sidepanel/PageSizeUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import * as Colors from 'ui-library/Colors';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import SortableHeaderCell, {
  SORT_ICON_DOWN,
  SORT_ICON_UP,
  SortIcon,
} from 'ui-library/simple-table/SortableHeaderCell';
import {readableTime} from 'util/DateUtil';

import PredictionBatchFilters from './PredictionBatchFilters';
import PredictionBatchStatusBadge from './PredictionBatchStatusBadge';
import type {SortDirection, SortField} from './PredictionBatchTypes';
import {hasActiveFilters, readFiltersFromUrl, readSortFromUrl, writeFiltersToUrl} from './runsUrlParams';
import type {PredictionBatchSearchFilters} from './usePredictionBatchSearch';
import {DEFAULT_FILTERS, usePredictionBatchSearch} from './usePredictionBatchSearch';

export interface PredictionBatchesTabProps {
  onNavigateToModels: () => void;
  onNavigateToPredictionJobs?: (batchId: string, batchName: string) => void;
  initialPredictionBatchId?: string | null;
}

export default function PredictionBatchesTab({
  onNavigateToModels,
  onNavigateToPredictionJobs,
  initialPredictionBatchId,
}: PredictionBatchesTabProps): JSX.Element {
  const [filters, setFilters] = useState<PredictionBatchSearchFilters>(() => {
    const urlFilters = readFiltersFromUrl();
    if (initialPredictionBatchId && !urlFilters.predictionBatchId) {
      return {...urlFilters, predictionBatchId: initialPredictionBatchId};
    }
    return urlFilters;
  });
  const [sortField, setSortField] = useState<SortField>(() => readSortFromUrl().key);
  const [sortDirection, setSortDirection] = useState<SortDirection>(() => readSortFromUrl().direction);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [offset, setOffset] = useState(0);

  const sort = useMemo(() => ({key: sortField, direction: sortDirection}), [sortField, sortDirection]);

  const {runs, pagination, isLoading, error, refresh} = usePredictionBatchSearch(
    filters,
    sort,
    offset,
    pageSize
  );

  useEffect(() => {
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.RUNS_TAB_VIEWED);
  }, []);

  useEffect(() => {
    writeFiltersToUrl(filters, sort);
  }, [filters, sort]);

  const handleFiltersChange = useCallback((newFilters: PredictionBatchSearchFilters) => {
    setFilters(newFilters);
    setOffset(0);
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.RUNS_TAB_FILTERED, {
      filterType: Object.keys(newFilters).filter(
        (k) =>
          newFilters[k as keyof PredictionBatchSearchFilters] !==
          DEFAULT_FILTERS[k as keyof PredictionBatchSearchFilters]
      ),
    });
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setOffset(0);
  }, []);

  const handleClearBatchFilter = useCallback(() => {
    setFilters((prev) => ({...prev, predictionBatchId: null}));
    setOffset(0);
  }, []);

  useEffect(() => {
    if (initialPredictionBatchId) {
      setFilters((prev) => {
        if (initialPredictionBatchId === prev.predictionBatchId) {
          return prev;
        }
        return {...prev, predictionBatchId: initialPredictionBatchId};
      });
      setOffset(0);
    }
  }, [initialPredictionBatchId]);

  const handleSortClick = useCallback(
    (field: SortField) => {
      if (field === sortField) {
        setSortDirection((prev: SortDirection) => (prev === 'desc' ? 'asc' : 'desc'));
      } else {
        setSortField(field);
        setSortDirection('desc');
      }
      setOffset(0);
      trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.RUNS_TAB_SORTED, {sortField: field});
    },
    [sortField]
  );

  const handleSetOffset = useCallback((newOffset: number) => {
    setOffset(newOffset);
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.RUNS_TAB_PAGE_CHANGED);
  }, []);

  const handlePageSizeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setOffset(0);
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.RUNS_TAB_PAGE_SIZE_CHANGED, {pageSize: newSize});
  }, []);

  const getSortIcon = (field: SortField): SortIcon =>
    field === sortField ? (sortDirection === 'desc' ? SORT_ICON_DOWN : SORT_ICON_UP) : null;

  const filtersActive = hasActiveFilters(filters);
  const isEmptyAndLoaded = runs.length === 0 && !isLoading;

  let content: JSX.Element;
  if (error != null) {
    content = (
      <div className={css(styles.emptyState)}>
        <div className={css(styles.emptyStateTitle)}>Something went wrong</div>
        <div className={css(styles.emptyStateSubtext)}>
          We couldn&apos;t load prediction batches. Please try again.
        </div>
        <Button buttonStyle={ButtonStyle.SECONDARY} size={ButtonSize.MEDIUM} text="Retry" onClick={refresh} />
      </div>
    );
  } else if (isEmptyAndLoaded && !filtersActive) {
    content = (
      <div className={css(styles.emptyState)}>
        <div className={css(styles.emptyStateTitle)}>No prediction batches yet</div>
        <div className={css(styles.emptyStateSubtext)}>Go to the Models tab to run your first model.</div>
        <Button
          buttonStyle={ButtonStyle.PRIMARY}
          size={ButtonSize.MEDIUM}
          text="Go to Models"
          onClick={onNavigateToModels}
        />
      </div>
    );
  } else if (isEmptyAndLoaded && filtersActive) {
    content = (
      <div className={css(styles.emptyState)}>
        <div className={css(styles.emptyStateTitle)}>No prediction batches match your filters</div>
        <div className={css(styles.emptyStateSubtext)}>Try adjusting your filters or clearing them.</div>
        <Button
          buttonStyle={ButtonStyle.SECONDARY}
          size={ButtonSize.MEDIUM}
          text="Clear filters"
          onClick={handleClearFilters}
        />
      </div>
    );
  } else {
    content = (
      <>
        <Table
          numRows={runs.length}
          isLoading={isLoading}
          pagination={pagination}
          setOffset={handleSetOffset}
          getRowKey={(rowIndex: number) => runs[rowIndex].id}
        >
          <Column
            header={<Cell>Name</Cell>}
            renderCell={(rowIndex: number) => (
              <Cell>
                {onNavigateToPredictionJobs ? (
                  <button
                    type="button"
                    className={css(styles.nameLink)}
                    onClick={() => {
                      onNavigateToPredictionJobs(runs[rowIndex].id, runs[rowIndex].name);
                    }}
                    {...getTestProps({component: 'PredictionBatchesTab', element: 'predictionBatchName'})}
                  >
                    {runs[rowIndex].name}
                  </button>
                ) : (
                  <span
                    {...getTestProps({component: 'PredictionBatchesTab', element: 'predictionBatchName'})}
                  >
                    {runs[rowIndex].name}
                  </span>
                )}
              </Cell>
            )}
          />
          <Column
            header={<Cell>Status</Cell>}
            renderCell={(rowIndex: number) => (
              <Cell>
                <PredictionBatchStatusBadge status={runs[rowIndex].status} />
              </Cell>
            )}
          />
          <Column
            header={<Cell>Model</Cell>}
            renderCell={(rowIndex: number) => (
              <Cell>
                <span {...getTestProps({component: 'PredictionBatchesTab', element: 'modelName'})}>
                  {runs[rowIndex].modelName}
                </span>
              </Cell>
            )}
          />
          <Column
            header={<Cell>Creator</Cell>}
            renderCell={(rowIndex: number) => (
              <Cell>
                <span {...getTestProps({component: 'PredictionBatchesTab', element: 'creatorName'})}>
                  {runs[rowIndex].creatorName}
                </span>
              </Cell>
            )}
          />
          <Column
            header={
              <SortableHeaderCell
                sortIcon={getSortIcon('createdAt')}
                onToggleSort={() => {
                  handleSortClick('createdAt');
                }}
              >
                Created
              </SortableHeaderCell>
            }
            renderCell={(rowIndex: number) => <Cell>{readableTime(runs[rowIndex].createdAt)}</Cell>}
          />
          <Column
            header={
              <SortableHeaderCell
                sortIcon={getSortIcon('modifiedAt')}
                onToggleSort={() => {
                  handleSortClick('modifiedAt');
                }}
              >
                Modified
              </SortableHeaderCell>
            }
            renderCell={(rowIndex: number) => <Cell>{readableTime(runs[rowIndex].modifiedAt)}</Cell>}
          />
          <Column
            header={<Cell>Num Jobs</Cell>}
            renderCell={(rowIndex: number) => (
              <Cell>
                <span {...getTestProps({component: 'PredictionBatchesTab', element: 'numJobs'})}>
                  {runs[rowIndex].numPredictionJobs ?? '—'}
                </span>
              </Cell>
            )}
          />
        </Table>
      </>
    );
  }

  return (
    <div
      className={css(styles.container)}
      {...getTestProps({component: 'PredictionBatchesTab', element: 'root'})}
    >
      <div className={css(styles.toolbarRow)}>
        <div className={css(styles.filtersLeft)}>
          <PredictionBatchFilters filters={filters} onFiltersChange={handleFiltersChange} />
          {filters.predictionBatchId != null && (
            <Chip
              size={ChipSize.SMALL}
              text={`Batch: ${filters.predictionBatchId}`}
              onRemove={handleClearBatchFilter}
            />
          )}
        </div>
        <label className={css(styles.pageSizeLabel)}>
          Rows per page:{' '}
          <select value={pageSize} onChange={handlePageSizeChange}>
            {AVAILABLE_PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>
      {content}
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  toolbarRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  filtersLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  pageSizeLabel: {
    fontSize: 13,
    color: Colors.GRAY_DARKER,
    flexShrink: 0,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '64px 24px',
    textAlign: 'center',
  },
  emptyStateTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: Colors.NEAR_BLACK,
    marginBottom: 8,
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: Colors.GRAY_DARKER,
    marginBottom: 16,
  },
  nameLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    color: Colors.BLUE,
    cursor: 'pointer',
    textAlign: 'left',
    ':hover': {
      textDecoration: 'underline',
    },
  },
});
